import debounce from 'lodash.debounce'
import { FormEventHandler, useCallback, useEffect, useState } from 'react'

const computeLinearGradient = (
  from: number,
  to: number,
  minRange: number,
  maxRange: number,
  isArabicLocale: boolean,
) => {
  const rangeDistance = maxRange - minRange
  const fromPositionPx = from - minRange
  const toPositionPx = to - minRange

  const fromPositionPercent = rangeDistance ? (fromPositionPx / rangeDistance) * 100 : 0
  const toPositionPercent = rangeDistance ? (toPositionPx / rangeDistance) * 100 : 0

  return `linear-gradient(
    to ${isArabicLocale ? 'left' : 'right'},
    #E5E5E5 0%,
    #E5E5E5 ${fromPositionPercent}%,
    #000 ${fromPositionPercent}%,
    #000 ${toPositionPercent}%, 
    #E5E5E5 ${toPositionPercent}%, 
    #E5E5E5 100%)`
}

type FilterProductFormPriceControllerProps = {
  initialMinValue?: number
  initialMaxValue?: number
  minRange: number
  maxRange: number
  onChange: (value: { min?: number; max?: number }) => void
  currency?: string
  isArabicLocale: boolean
}

const FilterProductFormPriceController = ({
  initialMinValue,
  initialMaxValue,
  minRange,
  maxRange,
  onChange,
  currency = '€',
  isArabicLocale,
}: FilterProductFormPriceControllerProps) => {
  const [from, setFrom] = useState(initialMinValue ?? minRange)
  const [to, setTo] = useState(initialMaxValue ?? maxRange)
  const isRangeDistanceZero = maxRange - minRange === 0

  useEffect(() => {
    setFrom(initialMinValue ?? minRange)
    setTo(initialMaxValue ?? maxRange)
  }, [initialMinValue, initialMaxValue, minRange, maxRange])

  const onSubmitChange = useCallback(
    debounce((value: { min?: number; max?: number }) => {
      onChange(value)
    }, 1000),
    [],
  )

  const onChangeRangeFrom: FormEventHandler<HTMLInputElement> | undefined = (e) => {
    const value = Number(e.currentTarget.value)
    const maxValue = to - 100
    setFrom(value >= maxValue ? maxValue : value)
    onSubmitChange({ min: value >= maxValue ? maxValue : value, max: to })
  }

  const onLostFocusFrom: FormEventHandler<HTMLInputElement> | undefined = () => {
    if (from < minRange) {
      setFrom(minRange)
    }
  }

  const onChangeRangeTo: FormEventHandler<HTMLInputElement> | undefined = (e) => {
    const value = Number(e.currentTarget.value)
    const minValue = from + 100
    setTo(value)
    onSubmitChange({ min: from, max: value <= minValue ? minValue : value })
  }

  const onLostFocusTo: FormEventHandler<HTMLInputElement> | undefined = () => {
    if (to > maxRange || to < from) {
      setTo(maxRange)
    }
  }

  return (
    <div className='filter-product-form-pricecontroller'>
      <div className='filter-product-form-price-controls'>
        <div className='price-control-wrapper price-control-wrapper-min'>
          <input
            type='number'
            value={from}
            onChange={onChangeRangeFrom}
            min={minRange}
            onBlur={onLostFocusFrom}
          />
          <span>{currency}</span>
        </div>
        <div className='price-control-wrapper price-control-wrapper-max'>
          <input
            type='number'
            value={to}
            onChange={onChangeRangeTo}
            max={maxRange}
            onBlur={onLostFocusTo}
          />
          <span>{currency}</span>
        </div>
      </div>
      <div className='filter-product-form-rangeprice'>
        <input
          className='filter-product-form-rangeprice-from'
          onInput={onChangeRangeFrom}
          type='range'
          min={isRangeDistanceZero ? from : minRange}
          max={isRangeDistanceZero ? to : maxRange}
          value={from}
          step='10'
        />
        <input
          className='filter-product-form-rangeprice-to'
          onInput={onChangeRangeTo}
          type='range'
          min={isRangeDistanceZero ? from : minRange}
          max={isRangeDistanceZero ? to : maxRange}
          value={to}
          style={{
            background: computeLinearGradient(from, to, minRange, maxRange, isArabicLocale),
          }}
          onBlur={onLostFocusTo}
          step='10'
        />
      </div>
    </div>
  )
}

export default FilterProductFormPriceController
